import { useMemo } from 'react';
import {
  useCampaignCardScale,
  useCampaignDataAttributes,
  useCampaignTypeContext,
} from 'features/campaigns';
import { useViewMode } from 'store/app/appStore';
import { useHasMany, useIsHeaderCollapsed } from '../../../store/campaignsStore';
import { getCardInfoTypes } from '../../../utils/campaignHelpers';
import { useCampaignInfoDisplay } from '../../../hooks/info/useCampaignInfoDisplay';
import { useCampaignStatus } from '../../../hooks/data/campaignStatus/useCampaignStatus';
import CampaignInfoDisplay from './components/CampaignInfoDisplay';
import originalHasManyStyles from './css/hasManyStyles.module.css';
import type { CampaignInfoDisplayStyles } from './css/base/CampaignInfoDisplayStyles';

const CARD_FRAME_INSET_PX = 2;

interface BottomCardInfoCampaignProps {
  campaignId?: string;
}

const BottomCardInfoCampaign: React.FC<BottomCardInfoCampaignProps> = ({ campaignId }) => {
  // Rendered only inside a card, which is always under a section provider.
  const campaignType = useCampaignTypeContext();
  const { getCampaignInfoStyles } = useCampaignInfoDisplay(campaignId);
  const dataAttributes = useCampaignDataAttributes({ campaignType });
  const hasMany = useHasMany(campaignType);
  const { isFinished, campaign } = useCampaignStatus(campaignId);
  const isJoined = campaign?.joined ?? false;
  const isCollapsed = useIsHeaderCollapsed();
  const viewMode = useViewMode();

  const campaignInfoDisplayStyles = useMemo(() => getCampaignInfoStyles(), [getCampaignInfoStyles]);
  const hasManyStyles = originalHasManyStyles as unknown as CampaignInfoDisplayStyles;

  const infoTypes = useMemo(
    () => getCardInfoTypes(campaignType, hasMany, isJoined),
    [hasMany, campaignType, isJoined],
  );

  // Collapsed cards keep the per-item scaling: there each item has its own narrow box to fit.
  const { titleRef: rowFitRef, scale } = useCampaignCardScale(infoTypes.join(), {
    recalculateKey: `${viewMode}-${hasMany}-${isFinished}`,
    disabled: isCollapsed,
  });


  return (
    <div
      className={`${campaignInfoDisplayStyles.campaignInfoDisplayContainer} ${hasMany ? hasManyStyles.campaignInfoDisplayContainer : ''}`}
      {...dataAttributes}
      data-finished={isFinished}
      ref={rowFitRef as React.RefObject<HTMLDivElement>}
        style={{
          paddingLeft: CARD_FRAME_INSET_PX,
          paddingRight: CARD_FRAME_INSET_PX,
          transform: scale < 1 ? `scale(${scale})` : undefined,
        }}
    >
        <CampaignInfoDisplay infoTypes={infoTypes} campaignId={campaignId} />
    </div>
  );
};

export default BottomCardInfoCampaign;
